import { DepressionContentPage } from "@/components/DepressionContentPage";

const SpravatoVsKetamine = () => (
  <DepressionContentPage
    seo={{
      title: "SPRAVATO vs Ketamine | Neuro Wellness TMS",
      description:
        "SPRAVATO® (esketamine) and IV ketamine are related but distinct treatments for depression. Learn the differences in administration, regulation, and insurance.",
      canonical: "/spravato-vs-ketamine",
      keywords:
        "SPRAVATO vs ketamine, esketamine vs ketamine, nasal spray vs IV ketamine, depression treatment difference, Coral Springs",
    }}
    badge="Treatment Comparison"
    heroTitle="SPRAVATO® vs. Ketamine: What's the Difference?"
    heroSubtitle="SPRAVATO® and IV ketamine are related treatments, but they are not the same. Understanding the distinction matters when you're considering treatment options."
    sections={[
      {
        heading: "Esketamine vs. Ketamine",
        variant: "text",
        body: (
          <p>
            SPRAVATO® contains esketamine, a specific form of ketamine. While
            they are chemically related, they are different formulations.
            SPRAVATO® is FDA-approved as a prescription medication for
            treatment-resistant depression. IV ketamine is administered as an
            infusion and is used off-label for depression.
          </p>
        ),
      },
      {
        heading: "How Each Is Administered",
        variant: "comparison",
        comparison: {
          headers: ["Feature", "SPRAVATO® (Esketamine)", "IV Ketamine"],
          rows: [
            {
              feature: "Form",
              columns: ["Prescription nasal spray", "Intravenous infusion"],
            },
            {
              feature: "Administration",
              columns: [
                "Self-administered under supervision",
                "Administered by clinical staff via IV",
              ],
            },
            {
              feature: "Setting",
              columns: [
                "Certified REMS treatment center",
                "Outpatient clinical setting",
              ],
            },
            {
              feature: "Monitoring",
              columns: [
                "2 hours observation after each dose",
                "Monitored throughout infusion",
              ],
            },
            {
              feature: "FDA status for depression",
              columns: ["FDA-approved", "Used off-label"],
            },
            {
              feature: "Insurance coverage",
              columns: [
                "Covered by many plans with prior auth",
                "Often not covered for depression",
              ],
            },
            {
              feature: "Schedule",
              columns: ["Twice weekly, tapering", "Series of infusions"],
            },
          ],
        },
      },
      {
        heading: "Medical Supervision",
        variant: "text",
        body: (
          <p>
            Both treatments require medical supervision. SPRAVATO® is given at a
            certified REMS center with a required monitoring period. IV ketamine
            is administered in a clinical setting with monitoring throughout the
            infusion. Neither is a take-home treatment.
          </p>
        ),
      },
      {
        heading: "Insurance Considerations",
        variant: "callout",
        callout:
          "Because SPRAVATO® is FDA-approved for depression, many insurance plans have an established process for covering it. IV ketamine, used off-label for depression, is often not covered by insurance. We can verify your benefits for whichever treatment you're considering.",
      },
      {
        heading: "Individualized Evaluation",
        variant: "text",
        body: (
          <p>
            Which treatment may be appropriate depends on your clinical history,
            current medications, and circumstances. We offer both, and a
            consultation is where we discuss which makes more sense for your
            situation.
          </p>
        ),
      },
    ]}
    faq={[
      {
        question: "Is SPRAVATO® just nasal ketamine?",
        answer:
          "No. SPRAVATO® contains esketamine, a specific form of ketamine, but it is a distinct FDA-approved medication with its own administration, regulation, and insurance considerations. It is not simply ketamine in nasal form.",
      },
      {
        question: "Which is more effective, SPRAVATO® or IV ketamine?",
        answer:
          "Neither is universally more effective. They are different treatments with different characteristics. Which may be appropriate depends on your individual situation and is determined during a clinical consultation.",
      },
      {
        question: "Does insurance cover IV ketamine for depression?",
        answer:
          "IV ketamine is used off-label for depression, and insurance often does not cover it. SPRAVATO®, being FDA-approved, is more commonly covered. We can verify your benefits for either treatment.",
      },
    ]}
    relatedLinks={[
      { title: "SPRAVATO® Overview", href: "/services/spravato" },
      {
        title: "IV Ketamine Treatment",
        href: "/ketamine-treatment-coral-springs-fl",
      },
      {
        title: "Compare Depression Treatments",
        href: "/compare-treatments",
      },
      { title: "TMS vs. SPRAVATO®", href: "/tms-vs-spravato" },
    ]}
    finalCtaText="Both treatments are available at our center. Speak with our team about which may make more sense for your situation."
  />
);

export default SpravatoVsKetamine;
